import { Box, Breadcrumbs, Link, Stack, Typography } from '@mui/material';
import { NavigateNext } from '@mui/icons-material';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

interface Crumb {
  label: string;
  to?: string;
}

interface Props {
  title: string;
  subtitle?: string;
  breadcrumbs?: Crumb[];
  action?: ReactNode;
  /**
   * The page is being shown inside another page — as a tab of Business Profile, for instance — so the
   * surrounding page already names it and says where it sits. Repeating a title and a breadcrumb trail
   * under one that already reads "Administration › Business Profile" only pushes the actual content
   * down the screen, so only the subtitle and the action survive.
   */
  embedded?: boolean;
  /** Optional icon shown before the title (e.g. the module's icon). */
  icon?: ReactNode;
  /** Optional inline content right after the title — a status chip, a count badge. */
  titleAdornment?: ReactNode;
  /** Tighter gap below the header — for pages whose content starts with its own toolbar or tabs. */
  dense?: boolean;
}

/** The action slot: wraps under the title on phones, sits on the right from `sm` up. */
function Actions({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
        flexShrink: 0,
        maxWidth: '100%',
      }}
    >
      {children}
    </Box>
  );
}

/**
 * Compact page heading: optional breadcrumb trail, the title (with an optional icon and adornment), a
 * one-line subtitle, and the page's actions. On phones the actions drop under the title and wrap.
 */
export default function PageHeader({
  title, subtitle, breadcrumbs, action, embedded, icon, titleAdornment, dense,
}: Props) {
  const rowSx = {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' } as const,
    justifyContent: 'space-between',
    alignItems: { xs: 'stretch', sm: 'center' },
    gap: { xs: 1.25, sm: 2 },
    minWidth: 0,
  };

  if (embedded) {
    // Nothing worth a row of its own: let the content start straight away.
    if (!subtitle && !action) return null;

    return (
      <Box sx={{ ...rowSx, mb: dense ? 1.5 : 2 }}>
        {subtitle
          ? <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>{subtitle}</Typography>
          : <Box sx={{ display: { xs: 'none', sm: 'block' } }} />}
        {action && <Actions>{action}</Actions>}
      </Box>
    );
  }

  return (
    <Box sx={{ ...rowSx, mb: dense ? 1.5 : { xs: 2, sm: 2.5 } }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {breadcrumbs && breadcrumbs.length > 0 && (
          <Breadcrumbs
            separator={<NavigateNext sx={{ fontSize: 14 }} />}
            sx={{
              mb: 0.25,
              '& .MuiBreadcrumbs-ol': { flexWrap: 'wrap' },
              '& .MuiBreadcrumbs-separator': { mx: 0.25 },
            }}
          >
            {breadcrumbs.map((c, i) =>
              c.to ? (
                <Link
                  key={i}
                  component={RouterLink}
                  to={c.to}
                  underline="hover"
                  color="text.secondary"
                  variant="caption"
                >
                  {c.label}
                </Link>
              ) : (
                <Typography key={i} variant="caption" color="text.primary" sx={{ fontWeight: 500 }}>
                  {c.label}
                </Typography>
              ),
            )}
          </Breadcrumbs>
        )}

        <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ minWidth: 0 }}>
          {icon && (
            <Box sx={{ display: 'flex', color: 'primary.main', flexShrink: 0 }}>{icon}</Box>
          )}
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontSize: { xs: '1.25rem', sm: '1.4rem', md: '1.5rem' },
              lineHeight: 1.25,
              minWidth: 0,
              overflowWrap: 'anywhere',
            }}
          >
            {title}
          </Typography>
          {titleAdornment}
        </Stack>

        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action && <Actions>{action}</Actions>}
    </Box>
  );
}
